import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from './icons';
import axios from 'axios';
import { useI18n, tx } from './i18n';
import { getQueuedQsos, removeQueuedQso, updateQueuedQsoError } from './shared';

const API = process.env.REACT_APP_API_URL || '';

// ── Widgety uzytkownika (nie-adminskie) ──────────────────────────────────────
// StationCallsignBar -- pasek "Pracuje jako" w loggerze.
// OfflineQueueModal  -- podglad kolejki QSO czekajacych na wyslanie.
//
// Wydzielone z Admin.js, bo to komponenty ZWYKLEGO uzytkownika. Trzymanie ich
// razem z panelem admina wymuszalo doladowanie calego (ciezkiego) kodu panelu
// u kazdego usera -- co niweczylo zysk z leniwego ladowania.

export function StationCallsignBar({ userProfile, value, onChange }) {
  const { t } = useI18n();
  const alts = userProfile?.alt_callsigns || [];

  // Znaki okolicznosciowe NADAJE ADMIN/MODERATOR -- user moze je tylko WYBRAC,
  // nie dodac. Powod: znaki SES (SN0HQ, HF100PL...) sa PRZYDZIELANE przez
  // organizatora akcji. Gdyby kazdy sam sobie dopisywal dowolny znak, moglby
  // logowac pod nim QSO i wysylac je do LoTW/eQSL -- czyli podszywac sie pod
  // cudza stacje i zasmiecac cudze logi. Backend tez tego pilnuje (proba
  // ustawienia alt_callsigns przez /api/auth/update jest ignorowana).
  //
  // Gdy user nie ma zadnych nadanych znakow -- pasek sie NIE POKAZUJE (nie ma
  // czego wybierac, a informowanie go o funkcji, ktorej nie moze uzyc, to szum).
  if (alts.length === 0) return null;

  const active = !!value;
  return (
    <div style={{margin:'0 0 14px',padding:'10px 14px',borderRadius:8,
      background: active?'rgba(255,187,0,0.1)':'var(--bg3)',
      border:`1px solid ${active?'rgba(255,187,0,0.4)':'var(--border)'}`,
      display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
      <span style={{fontSize:12,color:'var(--text-dim)',letterSpacing:'0.06em'}}>
        {t('admin.workingAs')}
      </span>
      <select className="input input--sm" value={value}
        onChange={e=>onChange(e.target.value)}
        style={{fontFamily:'var(--mono)',fontWeight:700,minWidth:150}}>
        <option value="">{userProfile?.callsign} ({t('logger.ownCallsign')})</option>
        {alts.map(c=><option key={c} value={c}>{c}</option>)}
      </select>
      {active&&(
        <span style={{fontSize:11,color:'#ffbb00'}}>
          {tx('⚠️ QSO zapisywane pod')} <strong>{value}</strong> {t('admin.alsoAdifLotw')}
        </span>
      )}
    </div>
  );
}


export function OfflineQueueModal({ onClose, notify, onQueueChanged, theme }) {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    getQueuedQsos().then(r => setItems(r.sort((a,b)=>(a.queuedAt||'').localeCompare(b.queuedAt||'')))).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const deleteItem = async (localId) => {
    if (!window.confirm(t('admin.confirmDeleteQueueItem'))) return;
    await removeQueuedQso(localId);
    load();
    onQueueChanged();
  };

  const retryOne = async (item) => {
    setBusyId(item.localId);
    const { localId, queuedAt, lastError, ...payload } = item;
    try {
      await axios.post(`${API}/api/qso`, payload);
      await removeQueuedQso(localId);
      notify(t('admin.qsoSent'));
      load();
      onQueueChanged();
    } catch (err) {
      const msg = err.response ? (err.response.data?.error || `${t('admin.errStatus')} ${err.response.status}`) : t('admin.stillNoConnection');
      if (err.response) await updateQueuedQsoError(localId, msg);
      notify(`${t('admin.failedToSend')}: ${msg}`, 'error');
      load();
    }
    setBusyId(null);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{width:560}} onClick={e=>e.stopPropagation()}>
        <div className="modal__header">
          <h3><Icon name="inbox" size={18} /> {t('admin.offlineQueueTitle')}</h3>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <p style={{fontSize:11,color:'var(--text-dim)',marginBottom:14,lineHeight:1.6}}>
          {t('admin.offlineQueueDesc')}
        </p>
        {!items?(
          <p style={{textAlign:'center',color:'var(--text-dim)'}}>{t('common.loadingDots')}</p>
        ):items.length===0?(
          <p style={{textAlign:'center',color:'var(--text-dim)'}}>{t('admin.queueEmpty')}</p>
        ):(
          <div style={{display:'flex',flexDirection:'column',gap:10,maxHeight:400,overflowY:'auto'}}>
            {items.map(item=>(
              <div key={item.localId} style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:8,padding:'10px 12px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',gap:8}}>
                  <strong style={{fontFamily:'var(--mono)'}}>{item.callsign||t('admin.noCallsign')}</strong>
                  <span style={{fontSize:11,color:'var(--text-dim)',whiteSpace:'nowrap'}}>
                    {item.date_time||'?'} · {item.frequency?tx('{0} MHz', [item.frequency]):'?'} · {item.mode||'?'}
                  </span>
                </div>
                {item.lastError&&(
                  <div style={{marginTop:6,fontSize:12,color:'#ff6666'}}>⚠️ {item.lastError}</div>
                )}
                <div style={{display:'flex',gap:8,marginTop:8}}>
                  <button className="btn btn--primary btn--sm" disabled={busyId===item.localId} onClick={()=>retryOne(item)}>
                    {busyId===item.localId?'…':t('admin.retryBtn')}
                  </button>
                  <button className="btn btn--ghost btn--sm" onClick={()=>deleteItem(item.localId)}><Icon name="trash" size={16} /> {t('admin.delete')}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
